import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { InmateClassification, InmateRecord, InmateStatus, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const CUSTODIAL_ROLES = ['CUSTODIAL_ADMIN'];
const CLASSIFICATIONS: InmateClassification[] = ['REMAND', 'MINIMUM', 'MEDIUM', 'MAXIMUM'];

// Same facility register m07-prisons validates against (src/service/facilities.ts).
const FACILITIES = [
  { code: 'MP-MAIN', name: 'Montagne Posée Prison', suitableFor: ['MINIMUM', 'MEDIUM', 'MAXIMUM'] },
  { code: 'MP-REMAND', name: 'Montagne Posée Remand Wing', suitableFor: ['REMAND'] },
  { code: 'COETIVY', name: 'Coëtivy Island Work Camp', suitableFor: ['MINIMUM'] },
];

/** Prisons — custodial records (M07). SENSITIVE classification: no public search, CUSTODIAL_ADMIN only. */
export function Inmates() {
  const { persona } = useAuth();
  const isCustodialAdmin = persona ? persona.roles.some((r) => CUSTODIAL_ROLES.includes(r)) : false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showAdmit, setShowAdmit] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m07/inmates?${params.toString()}`;
  }, [status, appliedQ]);

  const listQ = useApiQuery<Page<InmateRecord>>(path);

  return (
    <div>
      <PageHeader
        title="Inmates"
        subtitle="Custodial records — register number or NIN lookup only, CUSTODIAL_ADMIN only (M07)."
        actions={
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setAppliedQ(q);
            }}
          >
            <input className="field-input !w-44" placeholder="Reg no. or NIN…" value={q} onChange={(e) => setQ(e.target.value)} />
            <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              <option value="ADMITTED">Admitted</option>
              <option value="IN_CUSTODY">In custody</option>
              <option value="ESCAPED">Escaped</option>
              <option value="RELEASED">Released</option>
              <option value="DECEASED">Deceased</option>
            </select>
            <button type="submit" className="btn-primary">
              Search
            </button>
            {isCustodialAdmin ? (
              <button type="button" className="btn-secondary" onClick={() => setShowAdmit(true)}>
                + Manual admission
              </button>
            ) : null}
          </form>
        }
      />

      {!isCustodialAdmin ? <RoleNotice roles={CUSTODIAL_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No inmate records match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Reg no.</th>
                <th className="table-head">NIN</th>
                <th className="table-head">Classification</th>
                <th className="table-head">Facility</th>
                <th className="table-head">Admitted</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((inm) => (
                <tr key={inm.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(inm.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{inm.regNo}</td>
                  <td className="table-cell font-mono text-xs">{inm.nin}</td>
                  <td className="table-cell">
                    <StatusBadge status={inm.classification} />
                  </td>
                  <td className="table-cell text-xs text-slate-500">{inm.facilityCode}</td>
                  <td className="table-cell text-xs text-slate-500">{formatDate(inm.admissionDate)}</td>
                  <td className="table-cell">
                    <StatusBadge status={inm.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <InmateDetailModal
          id={selectedId}
          canAct={isCustodialAdmin}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            setSelectedId(null);
            listQ.reload();
          }}
        />
      ) : null}

      {showAdmit ? (
        <AdmitModal
          onClose={() => setShowAdmit(false)}
          onDone={() => {
            setShowAdmit(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function facilitiesFor(classification: string) {
  return FACILITIES.filter((f) => (f.suitableFor as string[]).includes(classification));
}

function InmateDetailModal({
  id,
  canAct,
  onClose,
  onChanged,
}: {
  id: string;
  canAct: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const q = useApiQuery<InmateRecord>(`/api/v1/m07/inmates/${id}`);
  const inm = q.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] = useState<'classify' | 'transfer' | 'release' | null>(null);
  const [classification, setClassification] = useState<InmateClassification>('MEDIUM');
  const [facilityCode, setFacilityCode] = useState('');
  const [transferReason, setTransferReason] = useState('');
  const [releaseReason, setReleaseReason] = useState<'SENTENCE_COMPLETED' | 'COURT_ORDER' | 'BAIL'>('SENTENCE_COMPLETED');

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={inm ? `${inm.regNo}` : 'Inmate record'} onClose={onClose} wide>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {inm ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="Status" value={<StatusBadge status={inm.status} />} />
            <Detail label="Classification" value={<StatusBadge status={inm.classification} />} />
            <Detail label="Facility" value={inm.facilityCode} />
            <Detail label="NIN" value={inm.nin} mono />
            <Detail label="Court order ref" value={inm.courtOrderRef} mono />
            <Detail label="Case no." value={inm.caseNumber ?? '—'} />
            <Detail label="Admission source" value={inm.admissionSource.replaceAll('_', ' ')} />
            <Detail label="Sentence" value={inm.isLifeSentence ? 'Life' : inm.sentenceMonths ? `${inm.sentenceMonths} months` : 'Not specified'} />
            <Detail label="Eligible release date" value={inm.eligibleReleaseDate ? formatDate(inm.eligibleReleaseDate) : '—'} />
            <Detail label="Parole eligible date" value={inm.paroleEligibleDate ? formatDate(inm.paroleEligibleDate) : '—'} />
          </div>

          {inm.dispatchNotes ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-sm text-amber-800">{inm.dispatchNotes}</div>
          ) : null}
          {inm.releaseDate ? <Detail label="Released" value={`${formatDate(inm.releaseDate)} — ${inm.releaseReason}`} /> : null}
          {inm.deceasedAt ? <Detail label="Deceased" value={`${formatDateTime(inm.deceasedAt)} — ${inm.deceasedNotes}`} /> : null}

          {inm.transfers && inm.transfers.length > 0 ? (
            <div>
              <div className="field-label">Transfer history</div>
              <ul className="space-y-1 text-sm">
                {inm.transfers.map((t) => (
                  <li key={t.id}>
                    {formatDate(t.transferredAt)}: {t.fromFacility} → {t.toFacility} ({t.reason})
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {!canAct ? (
            <RoleNotice roles={CUSTODIAL_ROLES} />
          ) : (
            <>
              {inm.status === 'ADMITTED' ? (
                <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                  <div className="text-sm font-semibold text-slate-700">Initial classification</div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Classification">
                      <select className="field-input" value={classification} onChange={(e) => setClassification(e.target.value as InmateClassification)}>
                        {CLASSIFICATIONS.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Facility">
                      <select className="field-input" value={facilityCode} onChange={(e) => setFacilityCode(e.target.value)}>
                        <option value="">Select a suitable facility</option>
                        {facilitiesFor(classification).map((f) => (
                          <option key={f.code} value={f.code}>
                            {f.name}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>
                  <button
                    className="btn-primary"
                    disabled={busy || !facilityCode}
                    onClick={() => call(() => api.post(`/api/v1/m07/inmates/${id}/classify`, { classification, facilityCode }))}
                  >
                    {busy ? 'Classifying…' : 'Classify — move to IN_CUSTODY'}
                  </button>
                </div>
              ) : null}

              {inm.status === 'IN_CUSTODY' ? (
                <div className="space-y-3">
                  {!action ? (
                    <div className="flex flex-wrap gap-2">
                      <button className="btn-secondary" onClick={() => setAction('transfer')}>
                        Transfer facility
                      </button>
                      <button className="btn-secondary" onClick={() => setAction('release')}>
                        Release
                      </button>
                      <button className="btn-danger" disabled={busy} onClick={() => call(() => api.post(`/api/v1/m07/inmates/${id}/escape`))}>
                        Record escape
                      </button>
                      <button
                        className="btn-secondary"
                        disabled={busy}
                        onClick={() => call(() => api.post(`/api/v1/m07/inmates/${id}/parole-eligibility/check`))}
                      >
                        Check parole eligibility
                      </button>
                    </div>
                  ) : action === 'transfer' ? (
                    <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                      <Field label="Transfer to facility">
                        <select className="field-input" value={facilityCode} onChange={(e) => setFacilityCode(e.target.value)}>
                          <option value="">Select a suitable facility</option>
                          {facilitiesFor(inm.classification).map((f) => (
                            <option key={f.code} value={f.code}>
                              {f.name}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Reason">
                        <input className="field-input" value={transferReason} onChange={(e) => setTransferReason(e.target.value)} />
                      </Field>
                      <div className="flex gap-2">
                        <button className="btn-secondary" onClick={() => setAction(null)}>
                          Cancel
                        </button>
                        <button
                          className="btn-primary"
                          disabled={busy || !facilityCode || transferReason.trim().length < 2}
                          onClick={() =>
                            call(() => api.post(`/api/v1/m07/inmates/${id}/transfer`, { toFacilityCode: facilityCode, reason: transferReason.trim() }))
                          }
                        >
                          {busy ? 'Transferring…' : 'Confirm transfer'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                      <Field label="Release reason">
                        <select className="field-input" value={releaseReason} onChange={(e) => setReleaseReason(e.target.value as typeof releaseReason)}>
                          <option value="SENTENCE_COMPLETED">Sentence completed</option>
                          <option value="COURT_ORDER">Court order</option>
                          <option value="BAIL">Bail</option>
                        </select>
                      </Field>
                      <p className="text-xs text-slate-400">Parole releases go through the Parole tab's start-supervision step, not here.</p>
                      <div className="flex gap-2">
                        <button className="btn-secondary" onClick={() => setAction(null)}>
                          Cancel
                        </button>
                        <button
                          className="btn-success"
                          disabled={busy}
                          onClick={() => call(() => api.post(`/api/v1/m07/inmates/${id}/release`, { reason: releaseReason }))}
                        >
                          {busy ? 'Releasing…' : 'Confirm release'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}

              {inm.status === 'ESCAPED' ? (
                <button className="btn-success" disabled={busy} onClick={() => call(() => api.post(`/api/v1/m07/inmates/${id}/recapture`))}>
                  Record recapture
                </button>
              ) : null}
            </>
          )}

          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">Last updated {formatDateTime(inm.updatedAt)}</div>
        </div>
      ) : null}
    </Modal>
  );
}

function AdmitModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [nin, setNin] = useState('');
  const [courtOrderRef, setCourtOrderRef] = useState('');
  const [caseNumber, setCaseNumber] = useState('');
  const [sentenceMonths, setSentenceMonths] = useState('');
  const [isLifeSentence, setIsLifeSentence] = useState(false);
  const [classification, setClassification] = useState<InmateClassification>('REMAND');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m07/inmates', {
        nin: nin.trim(),
        courtOrderRef: courtOrderRef.trim(),
        ...(caseNumber.trim() ? { caseNumber: caseNumber.trim() } : {}),
        ...(sentenceMonths.trim() && !isLifeSentence ? { sentenceMonths: Number.parseInt(sentenceMonths, 10) } : {}),
        isLifeSentence,
        classification,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to admit');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Manual custodial admission" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <p className="text-xs text-slate-400">Committals normally arrive via the M37 judgment-order feed — use this only for a manual/administrative admission.</p>
        <Field label="NIN">
          <input className="field-input font-mono" value={nin} onChange={(e) => setNin(e.target.value)} placeholder="e.g. 108-4471-2-2-19" />
        </Field>
        <Field label="Court order reference">
          <input className="field-input" value={courtOrderRef} onChange={(e) => setCourtOrderRef(e.target.value)} />
        </Field>
        <Field label="Case number (optional)">
          <input className="field-input" value={caseNumber} onChange={(e) => setCaseNumber(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Classification">
            <select className="field-input" value={classification} onChange={(e) => setClassification(e.target.value as InmateClassification)}>
              {CLASSIFICATIONS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Sentence (months)">
            <input className="field-input" type="number" min="1" disabled={isLifeSentence} value={sentenceMonths} onChange={(e) => setSentenceMonths(e.target.value)} />
          </Field>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={isLifeSentence} onChange={(e) => setIsLifeSentence(e.target.checked)} /> Life sentence
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" disabled={busy || !nin.trim() || !courtOrderRef.trim()} onClick={submit}>
            {busy ? 'Admitting…' : 'Admit'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
